import {
    IonBadge,
    IonButton,
    IonItem,
    IonItemDivider,
    IonItemGroup,
    IonLabel,
    IonList,
    IonListHeader,
    IonRefresher,
    IonRefresherContent,
    RefresherEventDetail,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import { useAtom } from 'jotai';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import LoadableContent from '../../components/content/LoadableContent';
import InterventionPage from '../../components/page/InterventionPage';
import { IAssetStateData } from '../../interfaces/IAssetState';
import { IInstallationRouteProps } from '../../interfaces/IRoute';
import { roomsWithDevicesAndStateAtom } from '../../store/Atoms';
import { getMeshStatusRequest } from '../../store/Requests';
import { THomeAssetData } from '../../types/HomeAssetData';
import { useCustomRouter } from '../../utils/hooks/useCustomRouter';

const Remove: React.FC<IInstallationRouteProps> = memo(({ match }) => {
    const router = useCustomRouter();
    const [value] = useAtom(roomsWithDevicesAndStateAtom);
    const [presentToast] = useIonToast();
    const [meshStatus, setMeshStatus] = useState<{
        [key: string]: IAssetStateData;
    }>();
    const allReset = useMemo(() => {
        return (
            meshStatus &&
            (Object.values(meshStatus).length === 0 ||
                Object.values(meshStatus).every(
                    (status) => status.online === false
                ))
        );
    }, [meshStatus]);
    const rooms = useMemo(
        () => (value.state === 'hasData' ? value.data : []),
        [value]
    );

    const onProceedHandler = useCallback(() => {
        router.push('inventory', 'forward', 'push');
    }, [router]);

    const loadMeshStatus = useCallback(async () => {
        try {
            const meshStatus = await getMeshStatusRequest();
            setMeshStatus(meshStatus);
        } catch (e) {
            presentToast({
                message: t`Oops, something went wrong while loading mesh status!`,
                duration: 5000,
                color: 'danger',
                buttons: [
                    {
                        text: t`Dismiss`,
                        role: 'cancel',
                    },
                ],
            });
        }
    }, [presentToast]);

    const handleRefresh = useCallback(
        async (event: CustomEvent<RefresherEventDetail>) => {
            await loadMeshStatus();
            event.detail.complete();
        },
        [loadMeshStatus]
    );

    const getStatusElement = useCallback(
        (device: THomeAssetData, status?: IAssetStateData) => {
            const badgeColor = status && status.online ? 'warning' : 'success';
            const badgeText =
                status && status.online ? t`Online` : t`Mesh Reset`;

            return (
                <IonBadge slot="end" color={badgeColor}>
                    {badgeText}
                </IonBadge>
            );
        },
        []
    );

    useEffect(() => {
        if (value.state === 'hasData') {
            loadMeshStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value.state]);

    return (
        <InterventionPage title={t`Remove`}>
            <LoadableContent
                value={{
                    ...value,
                    state: !meshStatus ? 'loading' : value.state,
                }}
            >
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent refreshingSpinner="circular"></IonRefresherContent>
                </IonRefresher>
                <IonList>
                    <IonListHeader color={allReset ? 'success' : 'warning'}>
                        <IonLabel>
                            <>
                                {allReset ? (
                                    <Trans>All devices are reset</Trans>
                                ) : (
                                    <>
                                        <Trans>Please reset all devices</Trans>
                                        <h6
                                            style={{ fontSize: '12px' }}
                                            dangerouslySetInnerHTML={{
                                                __html: t`Please reset all the devices by quickly pressing the reset button 10 times.<br/>Please refresh this page by pulling it down.`,
                                            }}
                                        />
                                    </>
                                )}
                            </>
                        </IonLabel>
                    </IonListHeader>
                    {allReset && (
                        <div className="ion-padding">
                            <IonButton
                                expand="block"
                                onClick={onProceedHandler}
                            >
                                <Trans>Proceed</Trans>
                            </IonButton>
                        </div>
                    )}
                    {rooms.map((room) => (
                        <IonItemGroup key={room.roomId}>
                            <IonItemDivider color="primary">
                                <IonLabel>{room.name}</IonLabel>
                            </IonItemDivider>
                            {room.devices.map((device: THomeAssetData) => {
                                const status =
                                    meshStatus &&
                                    meshStatus[device.homeAssetId];
                                return (
                                    <IonItem key={device.macAddress}>
                                        {getStatusElement(device, status)}
                                        <IonLabel>
                                            {device.macAddress}
                                        </IonLabel>
                                    </IonItem>
                                );
                            })}
                        </IonItemGroup>
                    ))}
                </IonList>
            </LoadableContent>
        </InterventionPage>
    );
});

export default Remove;
